import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Menu, X, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { waLink } from "@/lib/api";

const NAV = [
  { label: "Productos", href: "#productos", testid: "nav-products-link" },
  { label: "Ciencia", href: "#ciencia", testid: "nav-science-link" },
  { label: "Videos", href: "#videos", testid: "nav-videos-link" },
  { label: "Testimonios", href: "#testimonios", testid: "nav-testimonials-link" },
  { label: "FAQ", href: "#faq", testid: "nav-faq-link" },
];

export function Header({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${scrolled ? "glass-panel border-b border-emerald-500/15 shadow-lg shadow-black/40" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <a href="#inicio" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={content.logoUrl} alt={content.businessName} className="w-10 h-10 lg:w-12 lg:h-12 rounded-full object-cover ring-2 ring-emerald-500/40" />
          <div className="leading-tight">
            <span className="font-display font-800 font-extrabold tracking-tight text-sm lg:text-base block">{content.businessName}</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 block">Suplementos Premium</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} data-testid={n.testid} className="text-sm text-gray-300 hover:text-emerald-400 transition-colors duration-200 tracking-wide">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink(content.whatsapp, "Hola, quiero información sobre los suplementos 365 Cellular Energy.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className="hidden sm:flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200"
          >
            <MessageCircle className="w-4 h-4" />
            {content.whatsappDisplay}
          </a>
          <Link to="/admin" data-testid="nav-admin-btn" className="p-2 rounded-full border border-emerald-500/25 text-gray-400 hover:text-gold hover:border-yellow-600/40 transition-colors duration-200" aria-label="Acceso admin">
            <Lock className="w-4 h-4" />
          </Link>
          <button className="lg:hidden p-2 text-gray-300" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menú">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-panel border-t border-emerald-500/10 px-5 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="text-gray-200 text-base">
              {n.label}
            </a>
          ))}
          <a href={waLink(content.whatsapp, "Hola, quiero información sobre los suplementos 365 Cellular Energy.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-400 font-semibold">
            <MessageCircle className="w-4 h-4" /> WhatsApp {content.whatsappDisplay}
          </a>
        </div>
      )}
    </motion.header>
  );
}
